import { AppError } from "../errors/index.js";
/**
 * آلة حالة الكارت — **معرَّفة، لا مفتوحة**.
 *
 * في v2 كان `changeCardState` **يقبل أي هدفٍ من أي مصدر**: لا رسمَ انتقالات،
 * ولا موضعَ واحداً يُسأل «هل هذا مسموح؟». وكان `REOPENED` **لا يتميّز عن
 * `OPEN`** إلا في التقارير — حالةٌ في التعداد بلا سلوكٍ يخصّها، وهو أسوأ من
 * غيابها لأنها توحي بأن الفرق محسوب.
 *
 * فهنا خمسُ حالاتٍ لكلٍّ منها انتقالاتُها، **و«أُعيد فتحه» حالةٌ حقيقية**: لا
 * تُخرَج منها إلى «مفتوح» أبداً، فتبقى شاهدةً على أن هذا الشغل رجع مرّة.
 */
export const CARD_STATES = ["open", "in_progress", "blocked", "done", "reopened"];
/** الانتقالات المسموحة من كل حالة. ما ليس هنا مرفوض — لا مسكوتٌ عنه. */
const ALLOWED = {
    open: ["in_progress", "blocked", "done"],
    in_progress: ["open", "blocked", "done"],
    blocked: ["open", "in_progress"],
    /** **من «منجَز» لا يُخرَج إلا بإعادة فتحٍ مسمّاة** — لا بالرجوع إلى «مفتوح». */
    done: ["reopened"],
    /**
     * **ولا رجوعَ من «أُعيد فتحه» إلى «مفتوح»**.
     *
     * هذا هو ما يجعل الحالتين مختلفتين فعلاً لا بالاسم: الكارت الذي رجع مرّةً
     * يظلّ معلوماً أنه رجع، فلا يُغسَل تاريخُه بانتقالٍ إلى «مفتوح».
     */
    reopened: ["in_progress", "blocked", "done"],
};
/** إعادة الفتح: خروجٌ من «منجَز». تُفرَض عليها قاعدةُ المهلة في الحارس. */
export function isCardReopen(from, to) {
    return from === "done" && to === "reopened";
}
export function canMoveCard(from, to) {
    return (ALLOWED[from] ?? []).includes(to);
}
/** الحالات التي يمكن الانتقال إليها من هنا — تُبنى منها أزرار الشاشة */
export function cardMovesFrom(from) {
    return ALLOWED[from] ?? [];
}
/**
 * مهلة إعادة الفتح لمن أنهاه — **ساعةٌ واحدة** (المخطط §4).
 *
 * والمعنى: من أنهى كارتاً بالخطأ يصلحه بنفسه فوراً؛ وبعد الساعة تصير إعادة
 * الفتح سلطةَ قسمٍ أو شركة — فلا يُعيد أحدٌ فتح شغلٍ قديم ليخفي تأخيراً.
 *
 * وفي v2 لم تكن هذه القاعدة **موجودةً في الكود أصلاً**: لا نافذةَ وقتٍ ولا
 * فحصَ فاعلٍ ولا بوّابةَ دور، للكروت ولا للمهام. كانت مكتوبةً في وثيقةٍ وحدها.
 */
export const REOPEN_GRACE_MINUTES = 60;
/** هل تقع هذه الإعادة داخل مهلة صاحبها؟ */
export function withinReopenGrace(claim) {
    if (claim.doneById !== claim.actorId)
        return false;
    if (claim.doneAt === null)
        return false;
    const minutes = (claim.now.getTime() - claim.doneAt.getTime()) / 60_000;
    return minutes >= 0 && minutes <= REOPEN_GRACE_MINUTES;
}
/**
 * يرفض إعادة الفتح **بكودها**، ويسمّي القاعدة والمهلة.
 *
 * فمن يُرفض يعرف أن المهلة فاتت وكم كانت، لا «ليس مسموحاً».
 */
export function assertMayReopen(claim) {
    if (claim.elevated)
        return;
    if (withinReopenGrace(claim))
        return;
    throw new AppError("REOPEN_WINDOW_CLOSED", {
        context: {
            graceMinutes: String(REOPEN_GRACE_MINUTES),
            capability: "cards.change_state",
        },
    });
}
/**
 * يرفض الانتقال غير المسموح **بكوده**، ويسمّي الحالتين.
 *
 * فمن يرى الرفض يعرف من أين إلى أين، لا «حدث خطأ».
 */
export function assertCardMove(from, transition) {
    if (!canMoveCard(from, transition.to)) {
        throw new AppError("CARD_TRANSITION_NOT_ALLOWED", {
            context: { from, to: transition.to },
        });
    }
    if (transition.to === "blocked" && transition.reason.trim() === "") {
        throw new AppError("VALIDATION_FAILED", {
            fields: [{ field: "reason", messageKey: "cards.blockReasonRequired" }],
        });
    }
}
/** اسم الحالة في الشاشة — **مصدر واحد**، فلا يختلف كارتٌ عن عمودٍ عن تقرير */
export const CARD_STATE_LABEL = {
    open: "cards.state.open",
    in_progress: "cards.state.inProgress",
    blocked: "cards.state.blocked",
    done: "cards.state.done",
    reopened: "cards.state.reopened",
};
/** الحالات التي تُعدّ شغلاً قائماً — يخرج منها «منجَز» وحده */
export function isCardOpenWork(state) {
    return state !== "done";
}
//# sourceMappingURL=state.js.map